import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SplashScreen } from './components/SplashScreen';
import { LoginPage } from './components/LoginPage';
import { Layout } from './components/Layout';
import { DashboardPage } from './components/DashboardPage';
import { WorkflowsPage } from './components/WorkflowsPage';
import { WorkflowBuilder } from './components/WorkflowBuilder';
import { AgentsPage } from './components/AgentsPage';
import { AgentChat } from './components/AgentChat';
import { ProfilePage } from './components/ProfilePage';
import { MailPage } from './components/MailPage';
import { OffersPage } from './components/OffersPage';
import { AssetsPage } from './components/AssetsPage';
import { TokenizeAssetPage } from './components/TokenizeAssetPage';
import { InfracoinnDashboard } from './components/InfracoinnDashboard';
import { PublicWorkflowPage } from './components/PublicWorkflowPage';
import { HowItWorksPage } from './components/HowItWorksPage';
import { AutomationEditor } from './components/AutomationEditor';
import { PayrollPage } from './components/PayrollPage';

// ZAPHUB 2.1 - New Pages
import { ROIDashboard } from './components/ROIDashboard';
import { ComplianceReportPage } from './components/ComplianceReportPage';
import { BillingPage } from './components/BillingPage';
import { MarketplacePage } from './components/MarketplacePage';
import { IntegrationsPage } from './components/IntegrationsPage';
import { AdvancedAnalyticsDashboard } from './components/AdvancedAnalyticsDashboard';
import { AchievementsPage } from './components/AchievementsPage';
import { TemplatesPage } from './components/TemplatesPage';
import { WorkflowScheduler } from './components/WorkflowScheduler';
import { DataTransformerPanel } from './components/DataTransformerPanel';
import { PerformanceDashboard } from './components/PerformanceDashboard';

import { AppProviders } from './components/AppProviders';
import { useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Cargando ZAPHUB 2.0...</p>
        </div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
}

function App() {
  return (
    <AppProviders>
      <Router>
        <Routes>
          {/* Splash screen as default landing page */}
          <Route path="/" element={<SplashScreen />} />
          <Route path="/login" element={<LoginPage />} />
          {/* Public pages — no login required */}
          <Route path="/w/:id" element={<PublicWorkflowPage />} />
          <Route path="/como-funciona" element={<HowItWorksPage />} />

          <Route path="/dashboard" element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }>
            <Route index element={<DashboardPage />} />

            {/* Workflows */}
            <Route path="workflows" element={<WorkflowsPage />} />
            <Route path="workflows/new" element={<WorkflowBuilder />} />
            <Route path="workflows/:id" element={<WorkflowBuilder />} />
            <Route path="workflows/:id/edit" element={<WorkflowBuilder />} />

            {/* Automations (legacy routes) */}
            <Route path="automations" element={<WorkflowsPage />} />
            <Route path="automations/new" element={<AutomationEditor />} />
            <Route path="automations/:id/edit" element={<AutomationEditor />} />

            {/* Marketplace */}
            <Route path="marketplace" element={<MarketplacePage />} />
            <Route path="marketplace/:id" element={<MarketplacePage />} />

            {/* Integrations */}
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="integrations/:id" element={<IntegrationsPage />} />

            {/* Analytics */}
            <Route path="analytics" element={<AdvancedAnalyticsDashboard />} />
            <Route path="analytics/workflows/:id" element={<AdvancedAnalyticsDashboard />} />

            {/* Achievements */}
            <Route path="achievements" element={<AchievementsPage />} />
            <Route path="leaderboard" element={<AchievementsPage />} />

            {/* Payroll & Bonuses */}
            <Route path="payroll" element={<PayrollPage />} />

            {/* Assets & Blockchain */}
            <Route path="assets" element={<AssetsPage />} />
            <Route path="assets/new" element={<TokenizeAssetPage />} />
            <Route path="infracoinn" element={<InfracoinnDashboard />} />

            {/* AI Agents */}
            <Route path="agents" element={<AgentsPage />} />
            <Route path="agents/new" element={<AgentsPage />} />
            <Route path="agents/:id" element={<AgentChat />} />
            <Route path="agents/:id/chat" element={<AgentChat />} />
            <Route path="agents/:id/settings" element={<AgentsPage />} />

            {/* Mail & Offers */}
            <Route path="mail" element={<MailPage />} />
            <Route path="offers" element={<OffersPage />} />
            <Route path="offers/new" element={<OffersPage />} />

            {/* Profile */}
            <Route path="profile" element={<ProfilePage />} />

            {/* Templates */}
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="templates/new" element={<TemplatesPage />} />
            <Route path="templates/:id" element={<TemplatesPage />} />
            <Route path="templates/:id/edit" element={<TemplatesPage />} />

            {/* Workflow Scheduler */}
            <Route path="scheduler" element={<WorkflowScheduler />} />

            {/* Data Transformer Panel */}
            <Route path="data-transformer" element={<DataTransformerPanel />} />

            {/* Performance Dashboard */}
            <Route path="performance" element={<PerformanceDashboard />} />

            {/* ROI Dashboard */}
            <Route path="roi" element={<ROIDashboard />} />

            {/* Compliance Report */}
            <Route path="compliance" element={<ComplianceReportPage />} />

            {/* Billing */}
            <Route path="billing" element={<BillingPage />} />
          </Route>
        </Routes>
        <Toaster position="top-right" />
      </Router>
    </AppProviders>
  );
}

export default App;
